import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DashboardItem } from '../../models';
import { Docket } from '../../models/docket';
import { DocketReportParams } from '../../models/docket-report-params';
import { ApiClient } from '../api.client';
import { getQueryStringFromObject } from '../helpers/getQueryStringFromObject';
import { DocketRolloverModel } from '../models/docket-rollover.model';
import { DeliverableService } from '../deliveries/docket-delivery-list/deliverable-service.interface';
import { DeliveriesForDeliverable } from '../deliveries/docket-delivery-list/deliverable-service.interface';

@Injectable()
export class DocketService implements DeliverableService {
  apiUrl: string;
  defaultDocketRelations = [
    'items.item.product',
    'customer',
    'comments.user',
    'payments.user',
    'deliveries',
    'consumables.consumable'
  ];

  constructor(private http: ApiClient) {
    this.apiUrl = this.http.API_URL;
  }

  create(attributes) {
    return this.http.post<Docket>('dockets', attributes);
  }

  getDocket(id: number, includes: string[], attributes: string[] = []) {
    let qs =
      this.http.getWiths(includes) +
      '&' +
      this.http.urlSerializeArray(attributes, 'include');
    return this.http.get<Docket>(`dockets/${id}${qs}`);
  }

  update(id: number, docket: any) {
    return this.http.put('dockets/' + id, docket);
  }

  getByStatus(
    status: string,
    mapToHireItems = true,
    relations?
  ): Observable<Docket[] | DashboardItem[]> {
    if (!relations) {
      relations = this.defaultDocketRelations;
    }
    let statusClause = '';
    if (status !== '*') {
      statusClause = `&status=${encodeURI(status)}`;
    }
    let request = this.http.get<Docket[]>(
      `dockets?excludeDeleted=1${statusClause}`
    );

    if (mapToHireItems) {
      request = request.pipe(
        map((response) => {
          const docketItems = [];
          response.forEach((docket: Docket) => {
            docket.items.forEach((item) => {
              docketItems.push(<DashboardItem>{
                docket: docket,
                item: item
              });
            });
          });
          return docketItems;
        })
      );
    }
    return request;
  }

  getByStatusPaginated(params: DocketReportParams) {
    const qs = getQueryStringFromObject(params);
    return this.http.get<Docket[]>(`dockets?${qs}`);
  }

  print(id, location: string, download = false) {
    let qs = `?location=${location}`;
    if (download) {
      qs = qs + '&html=1';
    }
    return this.http.get<any>(`docket/view/${id}${qs}`);
  }

  print2(id, location: string, html = false) {
    let qs = `?location=${location}`;
    if (html) {
      qs = qs + '&html=1';
    }
    return this.http.getResponse(`docket/view/${id}${qs}`);
  }

  addSalesOrder(docketId) {
    return this.http.put(`docket/${docketId}/salesOrderNumber`, {});
  }

  getStatus(docketId) {
    return this.http.get<any>(`docket/${docketId}/status`);
  }

  bulkUpdateStatus(docketIds: number[], from: string, to: string) {
    return this.http.put(`docket/bulkUpdateStatus`, {
      ids: docketIds,
      fromStatus: from,
      toStatus: to
    });
  }

  generateReport(params: DocketReportParams, criteria) {
    const qs = getQueryStringFromObject(params);
    return this.http.post<{ data: any[] }>(`docket/report?${qs}`, criteria);
  }

  generateBalanceReport(params: DocketReportParams, criteria) {
    const qs = getQueryStringFromObject(params);
    return this.http.post<any[]>(`docket/balance-report?${qs}`, criteria);
  }

  generateReportXls(params: DocketReportParams, criteria) {
    const qs = getQueryStringFromObject(params);
    return this.http.post<any[]>(`docket/report?${qs}`, criteria, {
      responseType: 'blob'
    });
  }

  generateBalanceReportXls(params: DocketReportParams, criteria) {
    const qs = getQueryStringFromObject(params);
    return this.http.post<any[]>(`docket/balance-report?${qs}`, criteria, {
      responseType: 'blob'
    });
  }

  createFromBooking(bookingId: number, bookingItems) {
    return this.http.post(
      `docket/createFromBooking/${bookingId}`,
      bookingItems
    );
  }

  conflictCounts(docketId: number) {
    return this.http.get<{ id: number; conflict_count: number }[]>(
      `docket/${docketId}/conflict-counts`
    );
  }

  rolloverDockets(docketIds: number[]) {
    return this.http.post<DocketRolloverModel[]>(`docket/rollover`, {
      docket_ids: docketIds
    });
  }

  getDeliveries(deliverableId: number): Observable<DeliveriesForDeliverable> {
    return this.http.get<DeliveriesForDeliverable>(
      `docket/${deliverableId}/deliveries`
    );
  }
}
